import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, getAuthHeaders, apiRequest } from '../utils/api';

const CronCampaigns = () => {
  const { user } = useAuth();
  const [cronCampaigns, setCronCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'pending', 'approved', 'rejected'

  useEffect(() => {
    fetchCronCampaigns();
  }, []);

  const fetchCronCampaigns = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/admin/cron-campaigns');

      const data = await response.json();
      if (data.success) {
        setCronCampaigns(data.campaigns || data.cron_campaigns || []);
      } else {
        setMessage('Error fetching cron campaigns: ' + (data.error || 'Unknown error'));
        setCronCampaigns([]);
      }
    } catch (err) {
      setMessage('Network error: ' + err.message);
      setCronCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  // Per-row input state for shares/reposts
  const [shareInputsById, setShareInputsById] = useState({});

  useEffect(() => {
    // Initialize inputs from old/new metrics on load
    const init = {};
    if (cronCampaigns && Array.isArray(cronCampaigns)) {
    for (const c of cronCampaigns) {
      init[c.id] = {
        // Start empty to force explicit confirmation by the reviewer
        shares: '',
        reposts: '',
      };
      }
    }
    setShareInputsById(init);
  }, [cronCampaigns]);

  const handleApprove = async (cronCampaignId) => {
    try {
      setActionLoading(true);
      const current = shareInputsById[cronCampaignId] || { shares: '0', reposts: '0' };
      // Frontend validation: both fields required
      const sharesProvided = current.shares !== '' && !Number.isNaN(Number(current.shares)) && Number(current.shares) >= 0;
      const repostsProvided = current.reposts !== '' && !Number.isNaN(Number(current.reposts)) && Number(current.reposts) >= 0;
      if (!sharesProvided || !repostsProvided) {
        setMessage('Please enter both Reshares and Shares (non-negative).');
        setActionLoading(false);
        return;
      }
      const body = {
        new_shares: parseInt(current.shares) || 0,
        new_reposts: parseInt(current.reposts) || 0,
      };
      const response = await apiRequest(`/admin/cron-campaigns/${cronCampaignId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        setMessage('Error parsing response: ' + e.message);
        setActionLoading(false);
        return;
      }

      if (!response.ok) {
        let errorMsg = `HTTP ${response.status}: ${response.statusText}`;
        if (data) {
          if (data.detail) {
            if (Array.isArray(data.detail)) {
              errorMsg = data.detail.map(err => {
                if (typeof err === 'object' && err.msg) return err.msg;
                return String(err);
              }).join(', ');
            } else if (typeof data.detail === 'string') {
              errorMsg = data.detail;
            } else {
              errorMsg = JSON.stringify(data.detail);
            }
          } else if (data.error) {
            errorMsg = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
          } else if (data.message) {
            errorMsg = typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
          }
        }
        setMessage('Error approving campaign: ' + errorMsg);
        setActionLoading(false);
        return;
      }

      if (data.success) {
        setMessage('Campaign update approved successfully!');
        fetchCronCampaigns();
        // Reset inputs for this campaign
        setShareInputsById(prev => {
          const updated = { ...prev };
          delete updated[cronCampaignId];
          return updated;
        });
      } else {
        setMessage('Error approving campaign: ' + (data.error || data.message || 'Unknown error'));
      }
    } catch (err) {
      setMessage('Network error: ' + err.message);
      console.error('Error approving cron campaign:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const canApprove = (campaign) => {
    if (campaign.status !== 'pending') return false;
    const cur = shareInputsById[campaign.id];
    if (!cur) return false;
    const sharesOk = cur.shares !== '' && !Number.isNaN(Number(cur.shares)) && Number(cur.shares) >= 0;
    const repostsOk = cur.reposts !== '' && !Number.isNaN(Number(cur.reposts)) && Number(cur.reposts) >= 0;
    return sharesOk && repostsOk;
  };

  const handleReject = async (cronCampaignId) => {
    try {
      setActionLoading(true);
      const response = await apiRequest(`/admin/cron-campaigns/${cronCampaignId}/reject`, {
        method: 'POST',
      });

      const data = await response.json();
      if (data.success) {
        setMessage('Campaign update rejected successfully!');
        fetchCronCampaigns();
      } else {
        setMessage('Error rejecting campaign: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      setMessage('Network error: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (cronCampaignId) => {
    if (!window.confirm('Are you sure you want to delete this cron campaign update?')) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await apiRequest(`/admin/cron-campaigns/${cronCampaignId}`, {
        method: 'DELETE',
      });

      const data = await response.json();
      if (data.success) {
        setMessage('Cron campaign deleted successfully!');
        fetchCronCampaigns();
      } else {
        setMessage('Error deleting campaign: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      setMessage('Network error: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getMetricChangeColor = (oldValue, newValue) => {
    if (newValue > oldValue) return 'text-green-600';
    if (newValue < oldValue) return 'text-red-600';
    return 'text-gray-600';
  };

  const getMetricChangeIcon = (oldValue, newValue) => {
    if (newValue > oldValue) return '↗️';
    if (newValue < oldValue) return '↘️';
    return '➡️';
  };

  const filteredCampaigns = (cronCampaigns || []).filter(campaign => 
    filterStatus === 'all' || campaign.status === filterStatus
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }


  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Cron Campaign Updates</h1>
            <p className="text-gray-600">Review and approve automated campaign metric updates</p>
          </div>
        </div>
      </div>

      {message && (
        <div className="mb-6 p-4 rounded-md bg-blue-50 border border-blue-200">
          <p className="text-blue-800">{message}</p>
        </div>
      )}

      {/* Filter and Stats */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Campaign Updates</h2>
            <div className="flex items-center space-x-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <button
                onClick={fetchCronCampaigns}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>
        
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 mb-1">
                {(cronCampaigns || []).length}
              </div>
              <div className="text-sm text-gray-500">Total Updates</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600 mb-1">
                {(cronCampaigns || []).filter(c => c.status === 'pending').length}
              </div>
              <div className="text-sm text-gray-500">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 mb-1">
                {(cronCampaigns || []).filter(c => c.status === 'approved').length}
              </div>
              <div className="text-sm text-gray-500">Approved</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600 mb-1">
                {(cronCampaigns || []).filter(c => c.status === 'rejected').length}
              </div>
              <div className="text-sm text-gray-500">Rejected</div>
            </div>
          </div>
        </div>
      </div>

      {/* Campaign Updates Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Platform
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Views
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Likes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Comments
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reshares</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shares</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCampaigns.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-lg font-medium">No cron campaign updates found</p>
                      <p className="text-sm">Run a manual update to see pending changes here</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredCampaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {campaign.influencer_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {campaign.date}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {campaign.platform}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <span className={getMetricChangeColor(campaign.old_metrics.views, campaign.new_metrics.views)}>
                          {campaign.old_metrics.views.toLocaleString()} {getMetricChangeIcon(campaign.old_metrics.views, campaign.new_metrics.views)} {campaign.new_metrics.views.toLocaleString()}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <span className={getMetricChangeColor(campaign.old_metrics.likes, campaign.new_metrics.likes)}>
                          {campaign.old_metrics.likes.toLocaleString()} {getMetricChangeIcon(campaign.old_metrics.likes, campaign.new_metrics.likes)} {campaign.new_metrics.likes.toLocaleString()}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <span className={getMetricChangeColor(campaign.old_metrics.comments, campaign.new_metrics.comments)}>
                          {campaign.old_metrics.comments.toLocaleString()} {getMetricChangeIcon(campaign.old_metrics.comments, campaign.new_metrics.comments)} {campaign.new_metrics.comments.toLocaleString()}
                        </span>
                      </div>
                    </td>
                    {/* Reshares editable */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          className="w-24 px-2 py-1 border border-gray-300 rounded text-xs"
                          value={(shareInputsById[campaign.id]?.reposts) || ''}
                          onChange={(e) => setShareInputsById(prev => ({
                            ...prev,
                            [campaign.id]: { ...(prev[campaign.id] || {}), reposts: e.target.value }
                          }))}
                        />
                        <span className="text-xs text-gray-500">old: {campaign.old_metrics.reposts.toLocaleString()}</span>
                      </div>
                    </td>
                    {/* Shares editable */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          className="w-24 px-2 py-1 border border-gray-300 rounded text-xs"
                          value={(shareInputsById[campaign.id]?.shares) || ''}
                          onChange={(e) => setShareInputsById(prev => ({
                            ...prev,
                            [campaign.id]: { ...(prev[campaign.id] || {}), shares: e.target.value }
                          }))}
                        />
                        <span className="text-xs text-gray-500">old: {campaign.old_metrics.shares.toLocaleString()}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                        {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(campaign.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                      {campaign.status === 'pending' ? (
                        <div className="flex justify-center space-x-2">
                          <button
                            onClick={() => handleApprove(campaign.id)}
                            disabled={actionLoading || !canApprove(campaign)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                          >
                            ✓ Approve
                          </button>
                          <button
                            onClick={() => handleReject(campaign.id)}
                            disabled={actionLoading}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                          >
                            ✗ Reject
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleDelete(campaign.id)}
                          disabled={actionLoading}
                          className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                          🗑️ Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Information Panel */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-3">How Cron Campaign Updates Work</h3>
        <div className="text-blue-800 space-y-2">
          <p>• <strong>Automated Updates:</strong> When cron jobs run, they extract new metrics from social media URLs</p>
          <p>• <strong>Pending Approval:</strong> New metrics are saved to the "Cron Campaigns" section for review</p>
          <p>• <strong>Review Changes:</strong> Compare old vs new metrics to ensure accuracy</p>
          <p>• <strong>Approve/Reject:</strong> Approve updates to apply them to main campaigns, or reject if incorrect</p>
          <p>• <strong>Quality Control:</strong> This system ensures data accuracy before updating your main campaign data</p>
        </div>
      </div>
    </div>
  );
};

export default CronCampaigns;
